import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { resourceProfileRebuildQueryRootKey } from "../config";
import {
  executeResourceProfileRebuildTask,
  fetchResourceProfileRebuildTasks,
  previewResourceProfileRebuildTask,
} from "../repo/resourceProfileRebuildRepo";
import type { ResourceProfileRebuildRunResult } from "../types";

/** 格式化资源画像重算操作错误提示。 */
function formatActionError(prefix: string, error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return `${prefix}：${error.message}`;
  }
  return prefix;
}

/** 管理资源画像重算页面查询、预览与执行。 */
export function useResourceProfileRebuildRuntime() {
  const [latestResult, setLatestResult] = useState<ResourceProfileRebuildRunResult>();
  const [actionErrorMessage, setActionErrorMessage] = useState("");
  const query = useQuery({
    queryKey: resourceProfileRebuildQueryRootKey,
    queryFn: fetchResourceProfileRebuildTasks,
  });

  const previewMutation = useMutation({
    mutationFn: (taskKey: string) => previewResourceProfileRebuildTask(taskKey),
  });
  const executeMutation = useMutation({
    mutationFn: (taskKey: string) => executeResourceProfileRebuildTask(taskKey),
  });

  return {
    tasks: query.data?.items ?? [],
    loading: query.isLoading,
    errorMessage: query.error ? "资源画像重算任务加载失败，请稍后重试。" : actionErrorMessage,
    previewingTaskKey: previewMutation.isPending ? previewMutation.variables : undefined,
    executingTaskKey: executeMutation.isPending ? executeMutation.variables : undefined,
    latestResult,
    async handlePreview(taskKey: string) {
      setActionErrorMessage("");
      try {
        const result = await previewMutation.mutateAsync(taskKey);
        setLatestResult(result);
      } catch (error) {
        setActionErrorMessage(formatActionError("预览资源画像重算失败", error));
      }
    },
    async handleExecute(taskKey: string) {
      setActionErrorMessage("");
      try {
        const result = await executeMutation.mutateAsync(taskKey);
        setLatestResult(result);
      } catch (error) {
        setActionErrorMessage(formatActionError("执行资源画像重算失败", error));
      }
    },
  };
}
